import { DatePipe } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { ApplicationConfig, ErrorHandler, makeEnvironmentProviders, provideAppInitializer } from '@angular/core';
import {
  PreloadAllModules,
  provideRouter,
  Router,
  UrlSerializer,
  withComponentInputBinding,
  withInMemoryScrolling,
  withPreloading,
  withRouterConfig
} from '@angular/router';
import { createApollo, GRAPHQL_SERVICE_PROVIDER } from '@core/infra';
import Clarity from '@microsoft/clarity';
import * as Sentry from '@sentry/angular';
import { provideApollo } from 'apollo-angular';
import { MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';

import { ENV_LAUNCH_PROVIDER } from '@data-access/env-launch';
import { GOOGLE_PROVIDERS } from '@data-access/google';
import { PROVIDERS } from '@data-access/repository';
import { getAppConfigProvider } from '@data-access/utils';

import { TimeToFloatPipe } from '@shared/pipes';
import { SHARED_SERVICE_PROVIDERS } from '@shared/services';
import { STATE_PROVIDERS } from '@shared/state';
import { ensureActiveOrg, getActiveUserId, restoreActiveAccountUserId } from '@shared/utils';

import { environment } from '../environments/environment';

import { sentryConfig } from './config/sentry.config';
import { authInterceptor } from './interceptor/auth.interceptor';
import { errorInterceptor } from './interceptor/error.interceptor';
import { OrgPrefixUrlSerializer } from './utils/org-prefix-url-serializer';
import appRoutes from './app.routes';

// Initialize Sentry in production and qa
if (environment.production || environment.qa) {
  sentryConfig();
}

const CLARITY_PROJECT_ID = 'wwqnjo8m2i';
const CLARITY_ALLOWED_HOSTNAME = new URL(environment.currentHost).hostname;
const sentryProviders = makeEnvironmentProviders([
  {
    provide: ErrorHandler,
    useValue: Sentry.createErrorHandler()
  },
  {
    provide: Sentry.TraceService,
    deps: [Router]
  }
]);

const dataAccessProviders = [
  ...GRAPHQL_SERVICE_PROVIDER,
  ...PROVIDERS,
  ...GOOGLE_PROVIDERS,
  ...SHARED_SERVICE_PROVIDERS,
  ...ENV_LAUNCH_PROVIDER,
  ...STATE_PROVIDERS
];

const routerProviders = provideRouter(
  appRoutes,
  withComponentInputBinding(),
  withPreloading(PreloadAllModules),
  withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
  withRouterConfig({
    onSameUrlNavigation: 'reload'
  })
);

export const appConfig: ApplicationConfig = {
  providers: [
    provideAppInitializer(() => {
      if (environment.production && window.location.hostname === CLARITY_ALLOWED_HOSTNAME) {
        Clarity.init(CLARITY_PROJECT_ID);
      }
    }),
    provideAppInitializer(() => {
      const pathname = window.location.pathname;
      if (pathname.includes('onboarding')) {
        return;
      }
      const pathSegments = pathname.split('/').filter(Boolean);
      const orgSegmentIndex = pathSegments.indexOf('org');
      const orgIdFromUrl =
        orgSegmentIndex >= 0 && pathSegments[orgSegmentIndex + 1] ? Number(pathSegments[orgSegmentIndex + 1]) : 0;
      restoreActiveAccountUserId(orgIdFromUrl || 0);
      ensureActiveOrg(getActiveUserId());
    }),
    provideHttpClient(withInterceptors([authInterceptor, errorInterceptor])),

    routerProviders,
    { provide: UrlSerializer, useClass: OrgPrefixUrlSerializer },

    // Third-party providers
    provideApollo(() => createApollo()),
    providePrimeNG(),
    MessageService,

    // App-specific providers
    ...dataAccessProviders,
    getAppConfigProvider(environment),

    // Pipes
    DatePipe,
    TimeToFloatPipe,

    // Error handling and monitoring
    sentryProviders
  ]
};
